import * as Highcharts from "highcharts";
import { chartUtil } from "@/utils/chart.util";

export interface IConnectedChartsOptions {
  chartIds: Array<string>;
}

export function useConnectedCharts(options: IConnectedChartsOptions) {
  const chartsReadyStatus: Map<string, boolean> = new Map<string, boolean>();
  const chartIds = options.chartIds;

  function init(): void {
    chartIds.forEach((chartId: string) => setChartReadyStatus(chartId, false));
  }

  function setChartReadyStatus(chartId: string, isReady: boolean): void {
    chartsReadyStatus.set(chartId, isReady);
  }

  function getCurrentReadyStatusCharts(): Highcharts.Chart[] {
    const charts: Highcharts.Chart[] = Highcharts.charts.filter(
      // @ts-ignore
      (chart: Highcharts.Chart | undefined) => !!chart && chartsReadyStatus.has(chart.renderTo.id),
    ) as Highcharts.Chart[];

    return charts;
  }

  function zoomIn(xMin: number, xMax: number, currentChartId: string): void {
    const charts: Highcharts.Chart[] = getCurrentReadyStatusCharts();

    charts.forEach((chart: Highcharts.Chart) => {
      // @ts-ignore
      if (chart.renderTo.id === currentChartId) return;
      const ex: Highcharts.ExtremesObject = chart.xAxis[0].getExtremes();

      if (ex.min != xMin || ex.max != xMax) {
        chart.xAxis[0].setExtremes(xMin, xMax, true, true);

        // @ts-ignore
        if (!chart.resetZoomButton) {
          chart.showResetZoom();
        }
      }
    });
  }

  function resetZoom(currentChartId: string): void {
    const charts: Highcharts.Chart[] = getCurrentReadyStatusCharts();
    charts.forEach((chart: Highcharts.Chart) => {
      // @ts-ignore
      if (chart.renderTo.id === currentChartId) return;

      // @ts-ignore
      chart.resetZoomButton = chart.resetZoomButton?.destroy();
    });
  }

  function resizeConnectedCharts(): void {
    getCurrentReadyStatusCharts().forEach((chart: Highcharts.Chart) => chart.reflow());
  }

  function registerChartToCrosshair(chartId: string): void {
    setChartReadyStatus(chartId, true);
    if (_areAllChartsReady()) {
      _addChartsToCrosshair(chartIds);
    }
  }

  function unregisterChartFromCrosshair(chartId: string): void {
    setChartReadyStatus(chartId, false);
    _removeChartFromCrosshair(chartId);
  }

  function _addChartsToCrosshair(chartIds: Array<string>): void {
    const chartContainers: HTMLElement[] = _getChartContainers(chartIds);
    const charts: Highcharts.Chart[] = getCurrentReadyStatusCharts();
    chartUtil.addMultiChartsCrosshairEvents(charts, chartContainers);
  }

  function _removeChartFromCrosshair(chartId: string): void {
    const chartContainers: HTMLElement[] = _getChartContainers([chartId]);

    const charts: Highcharts.Chart[] = Highcharts.charts.filter(
      // @ts-ignore
      (chart: Highcharts.Chart | undefined) => !!chart && chart.renderTo.id === chartId,
    ) as Highcharts.Chart[];

    chartUtil.removeMultiChartsCrosshairEvent(charts, chartContainers);
  }

  function _getChart(name: string): HTMLElement | undefined {
    const el: HTMLElement | undefined | null = document.getElementById(name);
    return el?.parentElement || undefined;
  }

  function _getChartContainers(chartIds: Array<string>): HTMLElement[] {
    return chartIds.map((chartId: string) => _getChart(chartId)).filter((chart: HTMLElement | undefined) => !!chart);
  }

  function _areAllChartsReady(): boolean {
    return Array.from(chartsReadyStatus.values()).every((value: boolean) => value);
  }

  init();

  return {
    setChartReadyStatus,
    getCurrentReadyStatusCharts,
    zoomIn,
    resetZoom,
    resizeConnectedCharts,
    registerChartToCrosshair,
    unregisterChartFromCrosshair,
  };
}
